import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as WonIcon,
  Cancel as LostIcon,
  Assessment as PipelineIcon,
  Refresh,
} from '@mui/icons-material';
import { api } from '../../services/apiClient';
import { RouterLink } from '../../utils/linkNav';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  TypePill,
  StatusDot,
  StatusTone,
  TableEmptyState,
  ErrorState,
} from '../../components/common/pageKit';

interface Opportunity {
  id: number;
  leadId?: number;
  customerId?: number;
  customerName?: string;
  customerContact?: string;
  opportunityName: string;
  estimatedValue: number;
  estimatedValueFormatted: string;
  probability: number;
  expectedClosureDate?: string;
  stage: string;
  assignedTo?: number;
  assignedToName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface OpportunitySummary {
  totalOpportunities: number;
  openOpportunities: number;
  wonOpportunities: number;
  lostOpportunities: number;
  pipelineValue: number;
  pipelineValueFormatted: string;
  weightedPipelineValue: number;
  weightedPipelineValueFormatted: string;
  wonValue: number;
  wonValueFormatted: string;
}

const STAGES = [
  { value: 'PROSPECTING', label: 'Prospecting' },
  { value: 'QUALIFICATION', label: 'Qualification' },
  { value: 'PROPOSAL', label: 'Proposal' },
  { value: 'NEGOTIATION', label: 'Negotiation' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
];

const STAGE_TONE: Record<string, StatusTone> = {
  PROSPECTING: 'neutral',
  QUALIFICATION: 'info',
  PROPOSAL: 'info',
  NEGOTIATION: 'warning',
  WON: 'success',
  LOST: 'error',
};

const getStageLabel = (stage: string) => {
  const stageInfo = STAGES.find(s => s.value === stage);
  return stageInfo?.label || stage;
};

const getStageTone = (stage: string): StatusTone => STAGE_TONE[stage] || 'neutral';

const OpportunitiesPage: React.FC = () => {
  const navigate = useNavigate();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [summary, setSummary] = useState<OpportunitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteConfirm, setDeleteConfirm] = useState<Opportunity | null>(null);

  const fetchOpportunities = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStage) params.append('stage', filterStage);

      const data = await api.get(`/sales/opportunities?${params.toString()}`);
      setOpportunities(data.opportunities || []);
      setSummary(data.summary || null);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load opportunities');
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStage]);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  const handleDeleteOpportunity = async () => {
    if (!deleteConfirm) return;

    try {
      await api.delete(`/sales/opportunities/${deleteConfirm.id}`);
      setSnackbar({ open: true, message: 'Opportunity deleted successfully', severity: 'success' });
      setDeleteConfirm(null);
      fetchOpportunities();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to delete opportunity',
        severity: 'error'
      });
    }
  };

  const filteredOpportunities = opportunities.filter(opp => {
    const matchesSearch = opp.opportunityName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (opp.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesStage = !filterStage || opp.stage === filterStage;
    return matchesSearch && matchesStage;
  });

  const hasFilters = !!(searchTerm || filterStage);
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStage('');
  };

  // Stat cards read straight off the already-fetched `summary` payload --
  // null while loading (or before the first response lands) so a slow fetch
  // never briefly reads as "zero opportunities".
  const totalCount = loading ? null : summary?.totalOpportunities ?? null;
  const openPipelineValue = loading ? null : summary?.pipelineValueFormatted ?? null;
  const weightedPipelineValue = loading ? null : summary?.weightedPipelineValueFormatted ?? null;
  const wonCount = loading ? null : summary?.wonOpportunities ?? null;
  const lostCount = loading ? null : summary?.lostOpportunities ?? null;

  return (
    <Box>
      <PageHeader
        title="Sales Opportunities"
        subtitle="Manage sales pipeline and track opportunities"
        actions={
          <>
            <IconButton onClick={fetchOpportunities} disabled={loading}>
              <Refresh />
            </IconButton>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/sales/opportunities/new"
            >
              New Opportunity
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 2.5 }}>
        <StatCard title="Total Opportunities" value={totalCount} icon={<TrendingUpIcon />} color="#2563eb" />
        <StatCard title="Open Pipeline Value" value={openPipelineValue} icon={<PipelineIcon />} color="#0284c7" />
        <StatCard title="Weighted Pipeline" value={weightedPipelineValue} icon={<MoneyIcon />} color="#d97706" />
        <StatCard title="Won" value={wonCount} icon={<WonIcon />} color="#059669" />
        <StatCard title="Lost" value={lostCount} icon={<LostIcon />} color="#dc2626" />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      <FilterBar>
        <TextField
          size="small"
          placeholder="Search opportunities..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} />
          }}
        />
        <TextField
          select
          size="small"
          label="Stage"
          value={filterStage}
          onChange={(e) => setFilterStage(e.target.value)}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: <FilterIcon sx={{ fontSize: 18, color: 'text.disabled', mr: 1 }} />
          }}
        >
          <MenuItem value="">All Stages</MenuItem>
          {STAGES.map(stage => (
            <MenuItem key={stage.value} value={stage.value}>{stage.label}</MenuItem>
          ))}
        </TextField>

        <FilterSpacer />

        {hasFilters && (
          <Button size="small" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
        <Typography variant="body2" color="text.secondary">
          {filteredOpportunities.length} of {opportunities.length} opportunities
        </Typography>
      </FilterBar>

      {/* Opportunities Table */}
      <TableSurface>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Opportunity', 'Customer', 'Value', 'Probability', 'Stage', 'Expected Close', 'Actions'].map((h) => (
                  <TableCell key={h} sx={headerCellSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: 0 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <ErrorState message={error} onRetry={fetchOpportunities} />
                  </TableCell>
                </TableRow>
              ) : filteredOpportunities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0 }}>
                    <TableEmptyState
                      title="No opportunities found"
                      hint={hasFilters ? 'Try adjusting your search criteria' : 'Start by creating your first opportunity'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                filteredOpportunities.map((opportunity) => (
                  <TableRow key={opportunity.id} sx={bodyRowSx} hover>
                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {opportunity.opportunityName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {opportunity.customerName || '-'}
                        </Typography>
                        {opportunity.customerContact && (
                          <Typography variant="caption" color="text.secondary">
                            {opportunity.customerContact}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {opportunity.estimatedValueFormatted}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TypePill
                        label={`${opportunity.probability}%`}
                        tint={opportunity.probability >= 70 ? '#059669' : opportunity.probability >= 40 ? '#d97706' : '#64748b'}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusDot label={getStageLabel(opportunity.stage)} tone={getStageTone(opportunity.stage)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {opportunity.expectedClosureDate
                          ? new Date(opportunity.expectedClosureDate).toLocaleDateString('en-IN')
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          component={RouterLink}
                          to={`/sales/opportunities/${opportunity.id}/edit`}
                          title="Edit Opportunity"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteConfirm(opportunity)}
                          title="Delete Opportunity"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableSurface>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Opportunity</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteConfirm?.opportunityName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteOpportunity}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OpportunitiesPage;
